'use client';

import { useCallback, useState } from 'react';

import { getChaptersByWorkIdReq } from '@/features/fanfic-page/requests/chapters.requests';
import type { TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';

export interface UseChaptersQueryReturn {
  chapters: TChapterWithoutContent[];
  isLoading: boolean;
  error: Error | null;
  totalDocs: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  fetchChapters: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  loadAll: () => Promise<void>;
}

export interface UseChaptersQueryProps {
  workId: string;
  limit?: number;
}

export function useChaptersQuery({
  workId,
  limit = 10,
}: UseChaptersQueryProps): UseChaptersQueryReturn {
  const [chapters, setChapters] = useState<TChapterWithoutContent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const fetchChapters = useCallback(
    async (page: number = 1) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getChaptersByWorkIdReq({
          workId,
          page,
          limit,
        });

        // If page is 1, replace chapters; otherwise append
        setChapters((prev) => (page === 1 ? response.chapters : [...prev, ...response.chapters]));
        setTotalDocs(response.totalDocs);
        setTotalPages(response.totalPages);
        setCurrentPage(page);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch chapters'));
        console.error('Failed to fetch chapters:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [workId, limit],
  );

  const loadMore = useCallback(async () => {
    if (currentPage < totalPages && !isLoading) {
      await fetchChapters(currentPage + 1);
    }
  }, [currentPage, totalPages, isLoading, fetchChapters]);

  const loadAll = useCallback(async () => {
    if (currentPage < totalPages && !isLoading) {
      // Load all remaining chapters with a large limit
      try {
        setIsLoading(true);
        const response = await getChaptersByWorkIdReq({
          workId,
          page: 1,
          limit: totalDocs,
        });

        setChapters(response.chapters);
        setCurrentPage(totalPages);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch all chapters'));
        console.error('Failed to fetch all chapters:', err);
        setIsLoading(false);
      }
    }
  }, [currentPage, totalPages, isLoading, workId, totalDocs]);

  const hasMore = currentPage < totalPages;

  return {
    chapters,
    isLoading,
    error,
    totalDocs,
    totalPages,
    currentPage,
    hasMore,
    fetchChapters,
    loadMore,
    loadAll,
  };
}
